import { useMemo, useState, useEffect } from 'react';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import type { PlaylistItem, Source } from '@jwp/ott-common/types/playlist';
import { getSources } from '@jwp/ott-common/src/utils/sources';
import { useAccessStore } from '@jwp/ott-common/src/stores/AccessStore';
import { useAppConfig } from './useAppConfig';

export const useMediaSources = ({ item, baseUrl }: { item: PlaylistItem; baseUrl: string }) => {
  const config = useConfigStore((s) => s.config);
  const user = useAccountStore((s) => s.user);
  const passport = useAccessStore((s) => s.passport);
  const { featuredAdDeliveryMethod } = useAppConfig();
  const [sources, setSources] = useState<Source[]>([]);
  const [ssaiAdsData, setSsaiAdsData] = useState<any>([]);
  const effectiveAdDeliveryMethod = featuredAdDeliveryMethod || config?.adDeliveryMethod;

  useEffect(() => {
    const fetchSourcesAndAds = async () => {
      const result = await getSources({ item, baseUrl, config, user, passport });
      setSources(result);

      if (result?.[0]?.file) {
        if (effectiveAdDeliveryMethod === 'ssai') {
          const data = await fetchSessionInfo(result[0].file, config.siteId);
          setSsaiAdsData(data);
        }
      }
    };

    fetchSourcesAndAds();
  }, [item, baseUrl, config, user, passport]);

  return { sources, ssaiAdsData };
};

const fetchSessionInfo = async (SSAIUrl: string, siteId: string) => {
  try {
    const options = { method: 'GET' };
    const res = await fetch(SSAIUrl, options);
    const data = await res.text();
    const lines = data.split('\n');
    const sessionDataLine = lines.find((line) => line.startsWith('#EXT-X-SESSION-DATA') && line.includes('com.jwpc.ssai-session-endpoint'));

    if (!sessionDataLine) {
      console.warn('No SSAI session info found in the playlist.');
      return;
    }

    const uriMatch = sessionDataLine.match(/URI="ssai_session\/(mt-[^/]+)\/info\.json"/);
    if (!uriMatch || !uriMatch[1]) {
      console.warn('Failed to extract session ID.');
      return;
    }

    const sessionId = uriMatch[1];

    const streamUrl = lines.find((line) => line.trim().startsWith('http') && line.includes('.m3u8'));
    if (!streamUrl) {
      console.warn('No .m3u8 stream URL found.');
      return;
    }

    const streamRes = await fetch(streamUrl);

    if (!streamRes.ok) {
      console.error(`Failed to fetch stream .m3u8: ${streamRes.statusText}`);
      return;
    }

    const sessionInfoUrl = `https://cdn.jwplayer.com/v2/sites/${siteId}/ssai_session/${sessionId}.json`;
    const sessionRes = await fetch(sessionInfoUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    const sessionJson = await sessionRes.json();
    return sessionJson;
  } catch (error) {
    console.error('Error in SSAI session flow:', error);
  }
};
